"use client";

import { useState } from "react";
import ArrowIcon from "@/components/ui/ArrowIcon";
import TurnstileWidget from "@/components/ui/TurnstileWidget";

type Status = "idle" | "sending" | "sent" | "error";

const FIELD =
  "mt-2 w-full rounded-xl border border-ink/15 bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink/40 focus:border-brand focus:outline-none disabled:opacity-60";

export type ContactFormLabels = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  consent: string;
  submit: string;
  sending: string;
  success: string;
  errorRequired: string;
  errorEmail: string;
  errorGeneric: string;
};

export default function ContactForm({
  labels,
  locale = "fr",
}: {
  labels: ContactFormLabels;
  locale?: string;
}) {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const [token, setToken] = useState("");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const payload = {
      firstName: String(data.get("firstName") ?? "").trim(),
      lastName: String(data.get("lastName") ?? "").trim(),
      email: String(data.get("email") ?? "").trim(),
      phone: String(data.get("phone") ?? "").trim(),
      message: String(data.get("message") ?? "").trim(),
      website: String(data.get("website") ?? ""),
      turnstileToken: token,
      locale,
    };

    if (!payload.firstName || !payload.lastName || !payload.email || !payload.message) {
      setStatus("error");
      setError(labels.errorRequired);
      return;
    }

    setStatus("sending");
    setError("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };

      if (!res.ok) {
        setStatus("error");
        setError(
          body.error === "invalid_email" ? labels.errorEmail : labels.errorGeneric,
        );
        return;
      }

      form.reset();
      setStatus("sent");
    } catch {
      setStatus("error");
      setError(labels.errorGeneric);
    }
  }

  const busy = status === "sending";

  return (
    <form onSubmit={handleSubmit} noValidate className="mt-10 grid gap-5 sm:grid-cols-2">
      <div>
        <label htmlFor="firstName" className="text-sm font-medium text-ink">
          {labels.firstName}
        </label>
        <input
          id="firstName"
          name="firstName"
          type="text"
          required
          autoComplete="given-name"
          disabled={busy}
          className={FIELD}
        />
      </div>

      <div>
        <label htmlFor="lastName" className="text-sm font-medium text-ink">
          {labels.lastName}
        </label>
        <input
          id="lastName"
          name="lastName"
          type="text"
          required
          autoComplete="family-name"
          disabled={busy}
          className={FIELD}
        />
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="email" className="text-sm font-medium text-ink">
          {labels.email}
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          autoComplete="email"
          disabled={busy}
          className={FIELD}
        />
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="phone" className="text-sm font-medium text-ink">
          {labels.phone}
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          disabled={busy}
          className={FIELD}
        />
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="message" className="text-sm font-medium text-ink">
          {labels.message}
        </label>
        <textarea
          id="message"
          name="message"
          rows={6}
          required
          disabled={busy}
          className={FIELD}
        />
      </div>

      {/* Champ piège anti-robots : masqué et retiré de l'ordre de tabulation */}
      <div aria-hidden="true" className="hidden">
        <label htmlFor="website">Site web</label>
        <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="sm:col-span-2">
        <TurnstileWidget onToken={setToken} />
      </div>

      <p className="text-xs leading-relaxed text-ink/60 sm:col-span-2">
        {labels.consent}
      </p>

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-medium text-white transition hover:bg-brand-dark disabled:opacity-60"
        >
          {busy ? labels.sending : labels.submit}
          {!busy && <ArrowIcon className="h-3.5 w-3.5" />}
        </button>
      </div>

      <p aria-live="polite" className="text-sm sm:col-span-2">
        {status === "sent" && (
          <span className="font-medium text-brand">{labels.success}</span>
        )}
        {status === "error" && <span className="text-red-700">{error}</span>}
      </p>
    </form>
  );
}
